import React, {useEffect, useState} from "react";
import axiosInstance from "../../utils/axiosInstance";
import {PencilSquareIcon, TrashIcon} from "@heroicons/react/24/outline";
import {Pagination} from "../../components/ui";
import {usePagedList} from "../../hooks/usePagedList";
import Loader from "../../components/Loader";
import {Link} from "react-router-dom";
import {IoArrowBackSharp} from "react-icons/io5";

const AllUsers = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [users, setUsers] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [loadingToDelete, setLoadingToDelete] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNotification, setShowNotification] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axiosInstance.get("/users");
                setUsers(response.data);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoadingToDelete(true);
            await axiosInstance.delete(`/users/${userToDelete.id}`);
            setUsers(users.filter((user) => user.id !== userToDelete.id)); // Remove user from state
            setShowDeleteModal(false); // Close modal
            setUserToDelete(null);
            setLoadingToDelete(false);

            setShowNotification(true);

            setTimeout(() => {
                setShowNotification(false);
            }, 3000);
        } catch (error) {
            setError("Failed to delete user");
        }
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "ACTIVE":
            case "YES":
                return "bg-green-100 text-green-800";
            case "DEACTIVE":
            case "NO":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Filtered users based on search term
    const filteredUsers = users.filter(
        (user) =>
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.status.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paged = usePagedList(filteredUsers, 25, searchTerm);

    if (loading) {
        return (
            <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
                <Loader/>
            </div>
        );
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
            <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                {/* Go Back Button */}
                <button
                    onClick={() => window.history.back()} // This will navigate back to the previous page
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
                >
                    <IoArrowBackSharp size={24}/>
                </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
                All Users
            </h3>

            {/* Search Bar */}
            <div className="mb-4">
                <input
                    type="text"
                    className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500"
                    placeholder="Search by username, email, role, or status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <table className="min-w-full border-separate border-spacing-0 overflow-hidden rounded-[16px] border border-gray-200 bg-white text-sm dark:border-[#2c2c2e] dark:bg-[#1c1c1e] mb-5">
                <thead>
                <tr className="bg-gray-100 dark:bg-[#161617]">
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Total Users</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Total Credit Balance</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 font-bold text-emerald-600 dark:text-emerald-400">{users.length}</td>
                    <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 font-bold text-emerald-600 dark:text-emerald-400">
                        {users.reduce((sum, user) => sum + (user.credit_balance || 0), 0)}
                    </td>
                </tr>
                </tbody>
            </table>

            {/* Table for displaying users */}
            <table className="min-w-full border-separate border-spacing-0 overflow-hidden rounded-[16px] border border-gray-200 bg-white text-sm dark:border-[#2c2c2e] dark:bg-[#1c1c1e]">
                <thead>
                <tr className="bg-gray-100 dark:bg-[#161617]">
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">ID</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Username</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Email</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Country</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">CRS</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Verified</th>
                    <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Actions</th>
                </tr>
                </thead>
                <tbody>
                {paged.visible.map((user, key) => (
                    <tr key={user.id} className="transition hover:bg-gray-50 dark:hover:bg-[#232325]">
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                            {(paged.page - 1) * paged.pageSize + key + 1}
                        </td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{user.username}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{user.email}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{user.country}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">{user.credit_balance}</td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 border-gray-300">
                <span
                    className={`${getStatusBadgeClass(
                        (user.verified ? "YES" : "NO")
                    )}`}
                >
                  {user.verified ? "Yes" : "No"}
                </span>
                        </td>
                        <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 flex items-center gap-1">
                            <button
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400">
                                <Link to={`/admin/edit-user/${user.id}`}>
                                    <PencilSquareIcon className="h-5 w-5"/>
                                </Link>
                            </button>
                            <button
                                className="text-red-500 p-1 rounded hover:bg-red-600 hover:text-white flex items-center"
                                onClick={() => handleDeleteClick(user)}
                            >
                                <TrashIcon className="h-5 w-5"/>
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <Pagination
                page={paged.page}
                totalPages={paged.totalPages}
                onChange={paged.setPage}
                total={paged.total}
                pageSize={paged.pageSize}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-md p-6 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
                        <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Are you sure?</h2>
                        <p>
                            Do you really want to delete the user{" "}
                            <strong>{userToDelete.username}</strong>? This action cannot be
                            undone.
                        </p>
                        <div className="mt-6 flex justify-end space-x-4">
                            <button
                                className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-5 text-sm font-semibold text-gray-700 transition hover:border-red-400/60 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-200"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button
                                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-rose-500 px-6 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={handleDeleteConfirm}
                                disabled={loadingToDelete}
                            >
                                {loadingToDelete ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Notification Popup */}
            {showNotification && (
                <div
                    className="fixed right-4 top-20 z-[80] flex items-center gap-2.5 border-emerald-500/50 px-4 py-3 rounded-[22px] border border-gray-200 bg-white shadow-lg shadow-gray-900/5 dark:border-[#2c2c2e] dark:bg-[#1c1c1e] dark:shadow-black/40">
                    <div className="flex items-center">
                        <svg
                            className="h-5 w-5 text-emerald-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4"
                            ></path>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 2a10 10 0 110 20 10 10 0 010-20z"
                            ></path>
                        </svg>
                        <p className="font-semibold">User deleted successfully!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllUsers;
